/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import React from 'react';
import FormInput, { FormInputProps } from "components/generals/FormInput";
import { useFormikContext } from 'formik';

/**
 * Specialized input for blood pressure readings with automatic formatting.
 * Formats input as "systolic/diastolic" (e.g., "120/80").
 * 
 * Features:
 * - Auto-formatting to systolic/diastolic format
 * - Intelligent parsing of continuous digit input
 * - Handles 2-digit and 3-digit systolic values
 * - Limits diastolic to 2 digits
 * - Prevents invalid characters
 * - Single slash separator enforcement
 * - Formik integration
 * 
 * Input Handling:
 * - Allows only digits and single slash
 * - Auto-inserts slash when appropriate
 * - Parses "12080" as "120/80" intelligently
 * - Recognizes 100-200 range for 3-digit systolic
 * - Defaults to 2-digit systolic otherwise
 * 
 * Valid Formats:
 * - "120" → In progress
 * - "120/" → Waiting for diastolic
 * - "120/80" → Complete
 * - "12080" → Auto-formatted to "120/80"
 * 
 * @param name - Formik field name
 * @param readOnly - Disables input when true
 * @param error - Error state flag
 * @param placeholder - Placeholder text (default: "120/80")
 * @param otherProps - Additional FormInput props
 * @returns Formatted blood pressure input field
 */
const BloodPressureInput: React.FC<FormInputProps> = ({
  name,
  readOnly = false,
  error,
  placeholder = "120/80",
  ...otherProps
}) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const formatBloodPressure = (value: string): string => {
    const cleanValue = value.replace(/[^\d\/]/g, '');   
    if (!cleanValue) return '';
    if (cleanValue.includes('/')) {
      const parts = cleanValue.split('/');
      const systolic = parts[0].replace(/[^\d]/g, '');
      const diastolic = parts[1] ? parts[1].replace(/[^\d]/g, '').substring(0, 2) : ''; 
      
      if (!systolic) return '';
      const limitedSystolic = systolic.substring(0, 3);

      if (diastolic) {
        return `${limitedSystolic}/${diastolic}`;
      } else {
        return `${limitedSystolic}/`;
      }
    }
    
    const numbersOnly = cleanValue.replace(/[^\d]/g, '');
    
    if (numbersOnly.length <= 3) {
      return numbersOnly;
    }  
    let systolic, diastolic;
    
    if (numbersOnly.length >= 4) {
      const firstThree = numbersOnly.substring(0, 3);
      const firstTwo = numbersOnly.substring(0, 2);
      
      if (parseInt(firstThree) >= 100 && parseInt(firstThree) <= 200) {
        systolic = firstThree;
        diastolic = numbersOnly.substring(3, 5); 
      } else {
        systolic = firstTwo;
        diastolic = numbersOnly.substring(2, 4);
      }
      return `${systolic}/${diastolic}`;
    }
    return numbersOnly;
  };

  const handleBloodPressureChange = (e: React.ChangeEvent<any>) => {
    const inputValue = e.target.value;   
    const allowedChars = /^[\d\/]*$/;  

    if (!allowedChars.test(inputValue)) {
      return; 
    }
    const slashCount = (inputValue.match(/\//g) || []).length;
    if (slashCount > 1) {
      return; 
    }
    const formattedValue = formatBloodPressure(inputValue);   
    setFieldValue(name, formattedValue);
  };

  const handleBloodPressureKeyDown = (e: React.KeyboardEvent<any>) => {
    const input = e.target as any;
    const value = input.value;
    const cursorPosition = input.selectionStart || 0;
    const controlKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'];
    
    if (controlKeys.includes(e.key)) {
      if (e.key === 'Backspace' && cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 1) + value.substring(cursorPosition);
        const formattedValue = formatBloodPressure(newValue);   
        setFieldValue(name, formattedValue); 
        setTimeout(() => {
          if (input.setSelectionRange) {
            input.setSelectionRange(cursorPosition - 1, cursorPosition - 1);
          }
        }, 0);
      }
      return;
    }
    const allowedChars = /[\d\/]/;
    if (!allowedChars.test(e.key)) {
      e.preventDefault();
      return;
    }
    if (e.key === '/' && value.includes('/')) {
      e.preventDefault();
      return;
    }
  };

  return (
    <FormInput
      readOnly={readOnly}
      name={name}
      value={values[name] || ""}
      onChange={handleBloodPressureChange}
      onKeyDown={handleBloodPressureKeyDown}
      inputProps={{
        maxLength: 7,
      }}
      {...otherProps}
    />
  );
};

export default BloodPressureInput;